import { NgFor, NgIf } from "@angular/common";
import { Component, effect, ElementRef, Inject, OnDestroy, OnInit, signal, ViewChild } from "@angular/core";
import { LatLng, LatLngBounds } from "leaflet";
import { OfflineModeService } from "../../services/offline-mode.service";
import { PROJECT_PROVIDER } from "../../core";
import { LayerService, Region, Regions } from "../services/layer.service";
import { TransformationService } from "../services/transformation.service";
import { computeCentroid, computeConvexHull, getBoundsForPolygon, mergeOverlappingHulls } from "../utils";
import { cloneDeep } from "lodash";
import { ProjectPointsService } from "./project-points.service";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { Subject, takeUntil } from "rxjs";
import { GeoPackageAPI, GeometryData } from '@ngageoint/geopackage';
// @ts-ignore - shpjs hat keine TypeScript-Definitionen
import shp from 'shpjs';
// @ts-ignore - shapefile für robusteres Parsen
import * as shapefile from 'shapefile';
// @ts-ignore - libarchive-wasm für 7z-Archive
import { ArchiveReader, libarchiveWasm } from 'libarchive-wasm';
import { GpkgLayerSelectModalComponent, GpkgLayerInfo, GpkgGeometryType } from '../controls/gpkg-layer-select-modal.component';

const MAX_INDIVIDUAL_POLYGONS = 10;
const MAX_RESULT_REGIONS = 20; // Maximale Anzahl resultierender Bereiche
const MAX_IMPORT_POLYGONS = 500; // Maximale Anzahl importierbarer Polygone
const CLUSTER_DISTANCE_METERS = 500; // Start-Distanz für Clustering, wird bei Bedarf erhöht

interface RegionWithActivation {
	b: LatLngBounds;
	active: boolean;
}

@Component({
    selector: 'bp-project-regions',
    templateUrl: 'project-regions.component.html',
    styleUrls: ['project-regions.component.css', 'project-manager-section.style.css'],
		imports: [NgFor,NgIf],
    standalone: true
})
export class ProjectRegionsComponent implements OnInit, OnDestroy {
	regionsWithActivation = signal<RegionWithActivation[]>([]);
	private destroy$ = new Subject<void>();

	@ViewChild("geoJsonFileInput") geoJsonFileInput!: ElementRef<HTMLInputElement>;
	@ViewChild("shapefileCompanionsInput") shapefileCompanionsInput!: ElementRef<HTMLInputElement>;

	// Temporär gespeicherte .shp-Datei während wir auf Begleitdateien warten
	private pendingShpFile: File | null = null;

	constructor(
		@Inject(PROJECT_PROVIDER) private ps: any,
		public ls: LayerService,
		public oms: OfflineModeService,
		private ts: TransformationService,
		private pps: ProjectPointsService,
		private modalService: NgbModal
	) {
		effect(() => {
			if (this.ls.regions().regions) {
				this.regionsWithActivation.update(() => this.ls.regions().regions.map((r: Region) => ({ b: r.bounds, active: false } as RegionWithActivation)));
			}
		});

		// GeoJSON-Import-Event abonnieren (Dateien werden vom PointStore mitgeliefert)
		this.ls.geoJsonImportRequested.pipe(takeUntil(this.destroy$)).subscribe((files: File[]) => this.processImportedFiles(files));
	}

	ngOnInit() {
		const ra = this.ls.regions()?.regions.map((r: Region) => ({ b: r.bounds, active: false } as RegionWithActivation));
		this.regionsWithActivation.update(() => ra);
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}

	someActive() {
		return this.regionsWithActivation().some((r: RegionWithActivation) => r.active);
	}

	getRegionName(index: number): string {
		const names = this.pps.regionNames();
		// Prüfe beide Schlüsseltypen wegen JSON-Serialisierung (numerisch → String)
		return names[index] || (names as any)[String(index)] || `Bereich ${index + 1}`;
	}

	zoomToAll() {
		const regions = this.regionsWithActivation();
		if (!regions?.length) return;
		let bounds: LatLngBounds | null = null;
		regions.forEach(r => {
			if (bounds) bounds.extend(r.b);
			else bounds = cloneDeep(r.b);
		});
		if (bounds) this.ls.regionClicked.emit(bounds);
	}

	onClick(r: RegionWithActivation) {
		// Nur zum angeklickten Bereich zoomen
		this.ls.regionClicked.emit(r.b);
	}

	onDelete() {
		const newRegions = cloneDeep(this.ls.regions());
		const activeIndices = this.regionsWithActivation().map((r: RegionWithActivation, i: number) => r.active ? [i] : []).flat();
		const toRemove = new Set<number>(activeIndices);
		const totalRegions = newRegions.regions.length;

		// Namen und Filter neu zuordnen
		// Dabei explizite Namen für Bereiche setzen, die noch keinen haben (damit der angezeigte Name gleich bleibt)
		const oldNames = this.pps.regionNames();
		const oldFilters = this.pps.regionFilters();
		const newNames: Record<number, string> = {};
		const newFilters: Record<number, string> = {};

		// Mapping von alten Indizes auf neue Indizes berechnen
		let newIndex = 0;
		for (let oldIndex = 0; oldIndex < totalRegions; oldIndex++) {
			if (!toRemove.has(oldIndex)) {
				// Name übernehmen (prüfe beide Schlüsseltypen wegen JSON-Serialisierung)
				const oldName = oldNames[oldIndex] || (oldNames as any)[String(oldIndex)];
				if (oldName) {
					newNames[newIndex] = oldName;
				} else if (newIndex !== oldIndex) {
					// Bereich hatte keinen expliziten Namen, aber sein Placeholder würde sich ändern
					newNames[newIndex] = `Bereich ${oldIndex + 1}`;
				}

				// Filter übernehmen (prüfe beide Schlüsseltypen)
				const oldFilter = oldFilters[oldIndex] || (oldFilters as any)[String(oldIndex)];
				if (oldFilter) {
					newFilters[newIndex] = oldFilter;
				}
				newIndex++;
			}
		}

		this.pps.regionNames.set(newNames);
		this.pps.regionFilters.set(newFilters);

		newRegions.regions = newRegions.regions.filter((v: Region, i: number) => !toRemove.has(i));
		this.ls.regions.update(() => newRegions);
		this.ps.setProjectValue('regions', newRegions);
		// Namen/Filter-Zuordnung persistieren
		this.pps.saveSelection(false);
		this.ls.regionsChanged.emit();
	}

	onImportGeoJson() {
		this.geoJsonFileInput.nativeElement.click();
	}

	/** Verarbeitet importierte Dateien (vom PointStore oder lokalem File-Picker) */
	async processImportedFiles(files: File[]) {
		if (!files || files.length === 0) return;
		await this._processFiles(files);
	}

	async onGeoJsonFileAdded(event: Event) {
		if (!event?.target || !(event.target instanceof HTMLInputElement)) return;
		const fi = event.target as HTMLInputElement;
		if (!fi.files || fi.files.length === 0) return;

		const files = Array.from(fi.files);
		await this._processFiles(files);
		fi.value = '';
	}

	private async _processFiles(files: File[]) {
		const firstFile = files[0];
		const lower = firstFile.name.toLowerCase();

		try {
			let allPolygons: Region[] = [];

			// Prüfen ob Shapefile-Komponenten ausgewählt wurden
			const shpFile = files.find(f => f.name.toLowerCase().endsWith('.shp'));
			if (shpFile) {
				// Shapefile verarbeiten - versuche zugehörige Dateien zu finden
				allPolygons = await this.processShapeFileWithCompanions(shpFile, files);
			} else if (lower.endsWith('.geojson') || lower.endsWith('.json')) {
				// GeoJSON-Datei verarbeiten
				allPolygons = await this.processGeoJsonFile(firstFile);
			} else if (lower.endsWith('.gpkg')) {
				// GeoPackage-Datei verarbeiten
				allPolygons = await this.processGeoPackageFile(firstFile);
			} else if (lower.endsWith('.zip')) {
				// Shapefile als ZIP verarbeiten (enthält .shp/.dbf/.prj)
				allPolygons = await this.processShapeFile(firstFile);
			} else if (lower.endsWith('.7z')) {
				// Shapefile als 7z verarbeiten (enthält .shp/.dbf/.prj)
				allPolygons = await this.process7zFile(firstFile);
			} else {
				alert(`Dateityp nicht unterstützt: ${firstFile.name}\n\nUnterstützte Formate: .geojson, .json, .gpkg, .zip, .7z, .shp (mit .dbf)`);
				return;
			}

			if (allPolygons.length === 0) {
				// Bei Shapefile ohne .dbf wird ein zweiter Dialog geöffnet - keine Fehlermeldung
				if (!this.pendingShpFile) {
					alert('Keine Polygone in der Datei gefunden.');
				}
				return;
			}

			// Polygone als Bereiche hinzufügen (mit Clustering bei vielen Polygonen)
			const newRegions = this.processPolygonsToRegions(allPolygons);
			if (newRegions.length === 0) {
				alert('Konnte keine gültigen Bereiche berechnen.');
				return;
			}

			// Zu bestehenden Regions hinzufügen
			const regions = cloneDeep(this.ls.regions());
			const startIndex = regions.regions.length;
			regions.regions.push(...newRegions);

			// Debug: Zeige was gespeichert wird
			console.log('[project-regions] Speichere Regions:', regions.regions.length);
			regions.regions.forEach((r: any, i: number) => {
				console.log(`[project-regions] Region[${i}]:`, {
					type: r.type,
					hasBounds: !!r.bounds,
					hasPolygon: !!r.polygon,
					polygonLength: r.polygon?.length
				});
			});

			this.ls.regions.update(() => regions);
			this.ps.setProjectValue('regions', regions);

			// Bereichsnamen aus Dateiname setzen (VOR regionsChanged damit Karte korrekte Namen hat)
			const baseName = firstFile.name.replace(/\.(shp|geojson|json|gpkg|zip)$/i, '');
			this.setRegionNamesFromImport(startIndex, newRegions.length, baseName);

			// Neue Bereiche automatisch aktivieren
			const newIndices = Array.from({ length: newRegions.length }, (_, i) => startIndex + i);
			this.pps.activeRegionIndices.update(current => [...current, ...newIndices]);

			// Karte aktualisieren (NACH Namensetzung)
			this.ls.regionsChanged.emit();

		} catch (e) {
			console.error('Fehler beim Importieren:', e);
			alert(`Fehler beim Lesen der Datei: ${e instanceof Error ? e.message : 'Unbekannter Fehler'}`);
		}
	}

	/** Verarbeitet eine GeoJSON-Datei und extrahiert Polygone */
	private async processGeoJsonFile(file: File): Promise<Region[]> {
		const content = await this.readFileAsText(file);
		const geojson = JSON.parse(content);

		if (!this.isValidGeoJson(geojson)) {
			throw new Error('Ungültiges GeoJSON-Format. Die Datei muss ein FeatureCollection oder Feature mit Polygon/MultiPolygon-Geometrie enthalten.');
		}

		const crs = this.extractCrs(geojson);
		return this.extractPolygonsFromGeoJson(geojson, crs);
	}

	/** Verarbeitet eine GeoPackage-Datei und extrahiert Polygone aus allen Feature-Layern */
	private async processGeoPackageFile(file: File): Promise<Region[]> {
		const buffer = await this.readFileAsArrayBuffer(file);
		const geoPackage = await GeoPackageAPI.open(Buffer.from(buffer));
		const regions: Region[] = [];

		try {
			// Alle Feature-Tabellen durchgehen
			const featureTables = geoPackage.getFeatureTables();
			console.log('GeoPackage Feature-Tabellen:', featureTables);

			if (featureTables.length === 0) {
				console.warn('Keine Feature-Tabellen im GeoPackage gefunden');
				return regions;
			}

			// Layer-Auswahl-Dialog anzeigen (mit Geometrietypen)
			const selectedLayers = await this.showGpkgLayerSelectModal(file.name, geoPackage, featureTables);
			if (!selectedLayers || selectedLayers.length === 0) {
				// Benutzer hat abgebrochen oder keine Layer ausgewählt
				return regions;
			}

			// Nur ausgewählte Feature-Tabellen verarbeiten
			const selectedTableNames = selectedLayers.map(l => l.name);

			for (const tableName of selectedTableNames) {
				const featureDao = geoPackage.getFeatureDao(tableName);
				const srsId = featureDao.srs?.srs_id;
				const crs = srsId ? `EPSG:${srsId}` : null;
				const geomColumnName = featureDao.getGeometryColumnName();
				console.log(`Tabelle "${tableName}": CRS=${crs}, Geometrie-Spalte="${geomColumnName}"`);

				// Alle Features der Tabelle durchgehen
				const iterator = featureDao.queryForAll();
				let featureCount = 0;
				const geometryTypes: Record<string, number> = {};
				let noGeomCount = 0;
				let firstRow: any = null;

				for (const row of iterator) {
					featureCount++;
					if (!firstRow) {
						firstRow = row;
						console.log(`Erste Zeile Spalten:`, Object.keys(row));
						// Debug: Was ist in der geom-Spalte?
						const rawGeom = row[geomColumnName] as any;
						console.log(`Raw geom Typ:`, typeof rawGeom, rawGeom instanceof Uint8Array ? 'Uint8Array' : '', ArrayBuffer.isView(rawGeom) ? 'ArrayBufferView' : '');
						if (rawGeom && ArrayBuffer.isView(rawGeom)) {
							console.log(`Raw geom Länge:`, rawGeom.byteLength, 'Erste Bytes:', Array.from(new Uint8Array(rawGeom.buffer, rawGeom.byteOffset, Math.min(20, rawGeom.byteLength))));
						}
					}

					// Frühzeitig abbrechen wenn zu viele Polygone
					if (regions.length > MAX_IMPORT_POLYGONS) {
						throw new Error(
							`Die Datei enthält mehr als ${MAX_IMPORT_POLYGONS} Polygone.\n\n` +
							`Bitte verwenden Sie eine Datei mit weniger Polygonen oder fassen Sie die Polygone in einer GIS-Software zusammen.`
						);
					}

					// Geometrie direkt aus den rohen Bytes parsen
					const rawGeom = row[geomColumnName] as Uint8Array;
					if (!rawGeom || !ArrayBuffer.isView(rawGeom)) {
						noGeomCount++;
						continue;
					}

					// GeometryData aus Uint8Array erstellen
					let geojsonGeom: any = null;
					try {
						const geometryData = new GeometryData(Buffer.from(rawGeom));
						const geojsonResult = geometryData.toGeoJSON();
						// toGeoJSON() gibt direkt die Geometrie zurück, nicht ein Feature
						if (geojsonResult?.type === 'Feature') {
							geojsonGeom = geojsonResult.geometry;
						} else if (geojsonResult?.type) {
							// Direkte Geometrie (Polygon, MultiPolygon, etc.)
							geojsonGeom = geojsonResult;
						}
					} catch (e) {
						if (featureCount === 1) {
							console.warn('Fehler beim Parsen der Geometrie:', e);
						}
						noGeomCount++;
						continue;
					}

					if (!geojsonGeom) {
						noGeomCount++;
						continue;
					}
					geometryTypes[geojsonGeom.type] = (geometryTypes[geojsonGeom.type] || 0) + 1;
					this.processGeoPackageGeometry(geojsonGeom, crs, regions);
				}

				console.log(`Tabelle "${tableName}": ${featureCount} Features, ${noGeomCount} ohne Geometrie, Typen:`, geometryTypes);
			}
		} finally {
			geoPackage.close();
		}

		console.log(`GeoPackage: ${regions.length} Polygone extrahiert`);
		return regions;
	}

	/** Verarbeitet eine einzelne GeoPackage-Geometrie */
	private processGeoPackageGeometry(geojsonGeom: any, crs: string | null, regions: Region[]) {
		if (geojsonGeom.type === 'Polygon') {
			const polygon = this.coordinatesToLatLngs(geojsonGeom.coordinates[0], crs);
			if (polygon.length >= 3) {
				regions.push({
					type: 'polygon',
					bounds: getBoundsForPolygon(polygon),
					polygon: polygon
				});
			}
		} else if (geojsonGeom.type === 'MultiPolygon') {
			for (const poly of geojsonGeom.coordinates) {
				const polygon = this.coordinatesToLatLngs(poly[0], crs);
				if (polygon.length >= 3) {
					regions.push({
						type: 'polygon',
						bounds: getBoundsForPolygon(polygon),
						polygon: polygon
					});
				}
			}
		}
	}

	/**
	 * Verarbeitet ein Shapefile und versucht automatisch die zugehörigen Dateien zu laden.
	 * Falls Begleitdateien fehlen, wird ein zweiter Datei-Dialog geöffnet.
	 */
	private async processShapeFileWithCompanions(shpFile: File, selectedFiles: File[]): Promise<Region[]> {
		const baseName = shpFile.name.replace(/\.shp$/i, '');

		// Prüfen ob die Dateien bereits in der Auswahl enthalten sind
		const findInSelection = (ext: string): File | undefined => {
			const targetName = `${baseName}.${ext}`.toLowerCase();
			return selectedFiles.find(f => f.name.toLowerCase() === targetName);
		};

		const dbfFile = findInSelection('dbf');
		const prjFile = findInSelection('prj');
		const shxFile = findInSelection('shx');

		// Falls .dbf fehlt, speichere .shp und öffne Dialog für Begleitdateien
		if (!dbfFile) {
			this.pendingShpFile = shpFile;
			// Öffne Dialog für Begleitdateien
			setTimeout(() => {
				alert(
					`Bitte wählen Sie die zugehörigen Shapefile-Dateien aus:\n\n` +
					`• ${baseName}.dbf (erforderlich)\n` +
					`• ${baseName}.prj (optional, für Koordinatensystem)\n` +
					`• ${baseName}.shx (optional)\n\n` +
					`Tipp: Mit Strg+Klick (Desktop) oder langes Drücken (Mobil) können mehrere Dateien ausgewählt werden.`
				);
				this.shapefileCompanionsInput.nativeElement.click();
			}, 100);
			// Rückgabe leeres Array - die eigentliche Verarbeitung erfolgt in onShapefileCompanionsAdded
			return [];
		}

		// Alle Dateien vorhanden - direkt verarbeiten
		return this.processShapeFileParts(shpFile, dbfFile, prjFile || null, shxFile || null);
	}

	/** Handler für die Shapefile-Begleitdateien-Auswahl (.dbf, .prj, .shx) */
	async onShapefileCompanionsAdded(event: Event) {
		if (!event?.target || !(event.target instanceof HTMLInputElement)) return;
		const fi = event.target as HTMLInputElement;

		if (!fi.files || fi.files.length === 0 || !this.pendingShpFile) {
			this.pendingShpFile = null;
			fi.value = '';
			return;
		}

		const companionFiles = Array.from(fi.files);
		const shpFile = this.pendingShpFile;
		const baseName = shpFile.name.replace(/\.shp$/i, '').toLowerCase();

		// Begleitdateien nach Erweiterung zuordnen
		const findCompanion = (ext: string): File | undefined => {
			return companionFiles.find(f => f.name.toLowerCase() === `${baseName}.${ext}`);
		};

		const dbfFile = findCompanion('dbf');
		const prjFile = findCompanion('prj');
		const shxFile = findCompanion('shx');

		// Temporäre Datei zurücksetzen
		this.pendingShpFile = null;
		fi.value = '';

		if (!dbfFile) {
			alert('Die .dbf-Datei wurde nicht gefunden. Bitte stellen Sie sicher, dass Sie die richtige Datei ausgewählt haben.');
			return;
		}

		try {
			const allPolygons = await this.processShapeFileParts(shpFile, dbfFile, prjFile || null, shxFile || null);

			if (allPolygons.length === 0) {
				alert('Keine Polygone in der Datei gefunden.');
				return;
			}

			// Polygone als Bereiche hinzufügen
			const newRegions = this.processPolygonsToRegions(allPolygons);
			if (newRegions.length === 0) {
				alert('Konnte keine gültigen Bereiche berechnen.');
				return;
			}

			// Zu bestehenden Regions hinzufügen
			const regions = cloneDeep(this.ls.regions());
			const startIndex = regions.regions.length;
			regions.regions.push(...newRegions);
			this.ls.regions.update(() => regions);
			this.ps.setProjectValue('regions', regions);

			// Bereichsnamen aus Dateiname setzen (VOR regionsChanged damit Karte korrekte Namen hat)
			this.setRegionNamesFromImport(startIndex, newRegions.length, baseName);

			// Neue Bereiche automatisch aktivieren
			const newIndices = Array.from({ length: newRegions.length }, (_, i) => startIndex + i);
			this.pps.activeRegionIndices.update(current => [...current, ...newIndices]);

			// Karte aktualisieren (NACH Namensetzung)
			this.ls.regionsChanged.emit();
		} catch (e) {
			console.error('Fehler beim Importieren:', e);
			alert(`Fehler beim Lesen der Datei: ${e instanceof Error ? e.message : 'Unbekannter Fehler'}`);
		}
	}

	/** Setzt die Namen für importierte Bereiche basierend auf dem Dateinamen */
	private setRegionNamesFromImport(startIndex: number, count: number, baseName: string) {
		const newNames = { ...this.pps.regionNames() };
		for (let i = 0; i < count; i++) {
			const regionIndex = startIndex + i;
			// Bei mehreren Bereichen: Name mit Nummer
			newNames[regionIndex] = count > 1 ? `${baseName} (${i + 1})` : baseName;
		}
		this.pps.regionNames.set(newNames);
		// Namen persistieren damit sie beim Neuladen erhalten bleiben
		this.pps.saveSelection(false);
	}

	/** Verarbeitet .shp, .dbf und optional .prj/.shx Dateien */
	private async processShapeFileParts(shpFile: File, dbfFile: File, prjFile: File | null, shxFile: File | null): Promise<Region[]> {
		console.log('Shapefile-Import:', {
			shp: shpFile.name + ' (' + shpFile.size + ' bytes)',
			dbf: dbfFile.name + ' (' + dbfFile.size + ' bytes)',
			prj: prjFile ? prjFile.name + ' (' + prjFile.size + ' bytes)' : 'nicht vorhanden'
		});

		const shpBuffer = await this.readFileAsArrayBuffer(shpFile);
		const dbfBuffer = await this.readFileAsArrayBuffer(dbfFile);
		const prjText = prjFile ? await this.readFileAsText(prjFile) : undefined;

		console.log('Buffer-Größen:', { shp: shpBuffer.byteLength, dbf: dbfBuffer.byteLength });

		let detectedCrs: string | null = null;

		// CRS aus PRJ extrahieren (für spätere Transformation)
		if (prjText) {
			detectedCrs = this.detectCrsFromPrj(prjText);
			console.log('Erkanntes CRS aus PRJ:', detectedCrs, 'PRJ:', prjText.substring(0, 80));
		}

		// shapefile-Bibliothek für robustes Parsen verwenden
		const features: any[] = [];
		try {
			console.log('Parsing mit shapefile-Bibliothek...');
			const source = await shapefile.open(shpBuffer, dbfBuffer);
			let result = await source.read();
			while (!result.done) {
				features.push(result.value);
				// Frühzeitig abbrechen wenn zu viele Polygone
				if (features.length > MAX_IMPORT_POLYGONS) {
					throw new Error(
						`Die Datei enthält mehr als ${MAX_IMPORT_POLYGONS} Polygone.\n\n` +
						`Bitte verwenden Sie eine Datei mit weniger Polygonen oder fassen Sie die Polygone in einer GIS-Software zusammen.`
					);
				}
				result = await source.read();
			}
			console.log('Parsed:', features.length, 'features');
		} catch (e: any) {
			console.error('Fehler beim Parsen der Shapefile:', e);
			throw new Error(`Fehler beim Parsen der Shapefile: ${e instanceof Error ? e.message : e}`);
		}

		const geojson = {
			type: 'FeatureCollection' as const,
			features: features
		};

		const regions: Region[] = [];
		if (geojson.features.length > 0) {
			const crs = detectedCrs || this.extractCrs(geojson);
			console.log('Verwende CRS für Transformation:', crs);
			const extracted = this.extractPolygonsFromGeoJson(geojson, crs);
			regions.push(...extracted);
		}

		return regions;
	}

	/** Verarbeitet eine Shapefile-Datei (.zip oder .shp) und extrahiert Polygone */
	private async processShapeFile(file: File): Promise<Region[]> {
		const buffer = await this.readFileAsArrayBuffer(file);
		const geojson = await shp(buffer);

		// shpjs gibt entweder ein einzelnes FeatureCollection oder ein Array davon zurück
		const collections = Array.isArray(geojson) ? geojson : [geojson];
		const regions: Region[] = [];

		for (const fc of collections) {
			if (fc?.type === 'FeatureCollection' && Array.isArray(fc.features)) {
				// CRS aus Shapefile extrahieren (falls vorhanden)
				const crs = this.extractCrs(fc);
				const extracted = this.extractPolygonsFromGeoJson(fc, crs);
				regions.push(...extracted);
			}
		}

		return regions;
	}

	/** Verarbeitet eine 7z-Datei mit Shapefile-Inhalt und extrahiert Polygone */
	private async process7zFile(file: File): Promise<Region[]> {
		const buffer = await this.readFileAsArrayBuffer(file);

		try {
			// libarchive-wasm initialisieren und Archiv öffnen
			const libarchive = await libarchiveWasm();
			const reader = new ArchiveReader(libarchive, new Int8Array(buffer));

			// Shapefile-Dateien finden (.shp, .dbf, .prj)
			let shpBuffer: ArrayBuffer | undefined;
			let dbfBuffer: ArrayBuffer | undefined;
			let prjText: string | undefined;

			// Alle Entries durchlaufen und relevante Dateien extrahieren
			let entry: ReturnType<typeof reader.nextEntry>;
			while ((entry = reader.nextEntry()) !== null) {
				const fileName = entry.getPathname();
				const lower = fileName.toLowerCase();
				const size = entry.getSize();

				if (lower.endsWith('.shp') && size > 0) {
					const data = reader.readData(size);
					if (data) {
						// Int8Array zu ArrayBuffer konvertieren
						shpBuffer = new Uint8Array(data).buffer;
					}
				} else if (lower.endsWith('.dbf') && size > 0) {
					const data = reader.readData(size);
					if (data) {
						dbfBuffer = new Uint8Array(data).buffer;
					}
				} else if (lower.endsWith('.prj') && size > 0) {
					const data = reader.readData(size);
					if (data) {
						prjText = new TextDecoder().decode(new Uint8Array(data));
					}
				} else {
					// Andere Dateien überspringen
					reader.skipData();
				}
			}

			reader.free();

			if (!shpBuffer || !dbfBuffer) {
				console.error('Keine vollständigen Shapefile-Daten in der 7z-Datei gefunden');
				return [];
			}

			// Shapefile mit shapefile-Bibliothek parsen
			const features: any[] = [];
			const source = await shapefile.open(shpBuffer, dbfBuffer);
			let result = await source.read();
			while (!result.done) {
				features.push(result.value);
				result = await source.read();
			}

			if (features.length === 0) {
				return [];
			}

			// FeatureCollection erstellen
			const geojson = {
				type: 'FeatureCollection' as const,
				features: features,
				crs: prjText ? { type: 'name', properties: { name: prjText } } : undefined
			};

			// CRS aus PRJ-Datei extrahieren
			const crs = this.extractCrs(geojson);
			return this.extractPolygonsFromGeoJson(geojson, crs);

		} catch (e) {
			console.error('7z-Shapefile-Parsing fehlgeschlagen:', e);
			return [];
		}
	}

	/** Verarbeitet extrahierte Polygone zu Regions (mit Distanz-basiertem Clustering) */
	private processPolygonsToRegions(allPolygons: Region[]): Region[] {
		console.log(`processPolygonsToRegions: ${allPolygons.length} Polygone`);

		// Debug: Erstes Polygon prüfen
		if (allPolygons.length > 0) {
			const first = allPolygons[0];
			console.log('Erstes Polygon:', {
				type: first.type,
				hasBounds: !!first.bounds,
				boundsValid: first.bounds ? `${first.bounds.getSouth()},${first.bounds.getWest()} - ${first.bounds.getNorth()},${first.bounds.getEast()}` : 'n/a',
				hasPolygon: !!first.polygon,
				polygonLength: first.polygon?.length
			});
		}

		if (allPolygons.length <= MAX_INDIVIDUAL_POLYGONS) {
			// Puffer von 200m um jedes Polygon hinzufügen
			return allPolygons.map(region => {
				if (region.polygon && region.polygon.length >= 3) {
					const bufferedPolygon = this.addBufferToPolygon(region.polygon, 200);
					return {
						type: 'polygon' as const,
						bounds: getBoundsForPolygon(bufferedPolygon),
						polygon: bufferedPolygon
					};
				}
				return region;
			});
		}

		// Distanz schrittweise erhöhen bis max. MAX_RESULT_REGIONS Cluster entstehen
		let distance = CLUSTER_DISTANCE_METERS;
		let clusters = this.clusterPolygonsByDistance(allPolygons, distance);
		console.log(`Initiales Clustering mit ${distance}m: ${clusters.length} Cluster`);

		// Wenn zu viele Cluster, Distanz verdoppeln bis passend
		while (clusters.length > MAX_RESULT_REGIONS && distance < 50000) {
			distance *= 2;
			clusters = this.clusterPolygonsByDistance(allPolygons, distance);
			console.log(`Clustering mit ${distance}m: ${clusters.length} Cluster`);
		}

		// Für jeden Cluster nur die Bounding-Box verwenden (viel schneller als convex hull)
		const newRegions: Region[] = [];
		console.log(`Verarbeite ${clusters.length} Cluster`);

		// Debug: ersten Cluster genauer untersuchen
		if (clusters.length > 0 && clusters[0].length > 0) {
			const firstRegion = clusters[0][0];
			console.log('Erste Region im ersten Cluster:', {
				bounds: firstRegion.bounds,
				polygon: firstRegion.polygon,
				polygonLength: firstRegion.polygon?.length,
				firstPoint: firstRegion.polygon?.[0]
			});
		}

		for (const cluster of clusters) {
			// Alle Punkte des Clusters sammeln für konvexe Hülle
			const allPoints: LatLng[] = [];

			for (const region of cluster) {
				if (region.polygon && region.polygon.length > 0) {
					for (const point of region.polygon) {
						if (isFinite(point.lat) && isFinite(point.lng)) {
							allPoints.push(point);
						}
					}
				}
			}

			if (allPoints.length >= 3) {
				// Konvexe Hülle berechnen für optimalen Umriss
				const hull = computeConvexHull(allPoints);
				if (hull.length >= 3) {
					// Puffer von 200m um die Hülle hinzufügen
					const bufferedHull = this.addBufferToPolygon(hull, 200);
					newRegions.push({
						type: 'polygon',
						bounds: getBoundsForPolygon(bufferedHull),
						polygon: bufferedHull
					});
				}
			}
		}

		// Überlappende Bereiche zusammenführen
		const mergedPolygons = mergeOverlappingHulls(newRegions.map(r => r.polygon!));
		const mergedRegions = mergedPolygons.map(polygon => ({
			type: 'polygon' as const,
			bounds: getBoundsForPolygon(polygon),
			polygon: polygon
		}));

		console.log(`Ergebnis: ${newRegions.length} Bereiche -> ${mergedRegions.length} nach Zusammenführung`);

		if (allPolygons.length > MAX_INDIVIDUAL_POLYGONS) {
			alert(`Die Datei enthält ${allPolygons.length} Polygone. Es wurden ${mergedRegions.length} Bereiche gebildet.`);
		}

		return mergedRegions;
	}

	/**
	 * Clustert Polygone basierend auf räumlicher Distanz (Union-Find Algorithmus)
	 * Polygone deren Zentroide näher als maxDistanceMeters sind, werden zusammengefasst
	 */
	private clusterPolygonsByDistance(polygons: Region[], maxDistanceMeters: number): Region[][] {
		const n = polygons.length;
		if (n === 0) return [];

		// Zentroide berechnen - aus Polygon oder aus Bounds-Mittelpunkt
		const centroids = polygons.map(region => {
			if (region.polygon && region.polygon.length > 0) {
				return computeCentroid(region.polygon);
			} else if (region.bounds) {
				return region.bounds.getCenter();
			}
			// Fallback
			return new LatLng(0, 0);
		});

		// Union-Find Datenstruktur
		const parent = Array.from({ length: n }, (_, i) => i);
		const rank = new Array(n).fill(0);

		const find = (x: number): number => {
			if (parent[x] !== x) {
				parent[x] = find(parent[x]); // Pfadkompression
			}
			return parent[x];
		};

		const union = (x: number, y: number) => {
			const px = find(x);
			const py = find(y);
			if (px === py) return;
			// Union by rank
			if (rank[px] < rank[py]) {
				parent[px] = py;
			} else if (rank[px] > rank[py]) {
				parent[py] = px;
			} else {
				parent[py] = px;
				rank[px]++;
			}
		};

		// Distanz in Grad umrechnen (ungefähr, für Mitteleuropa)
		// 1 Grad Latitude ≈ 111km, 1 Grad Longitude ≈ 70km bei 50°N
		const maxDistDegrees = maxDistanceMeters / 111000;

		// Alle Paare prüfen und vereinigen wenn nah genug
		for (let i = 0; i < n; i++) {
			for (let j = i + 1; j < n; j++) {
				const dLat = centroids[i].lat - centroids[j].lat;
				const dLng = (centroids[i].lng - centroids[j].lng) * 0.63; // Korrektur für Längengrad
				const distDegrees = Math.sqrt(dLat * dLat + dLng * dLng);

				if (distDegrees <= maxDistDegrees) {
					union(i, j);
				}
			}
		}

		// Cluster aus Union-Find extrahieren
		const clusterMap = new Map<number, Region[]>();
		for (let i = 0; i < n; i++) {
			const root = find(i);
			if (!clusterMap.has(root)) {
				clusterMap.set(root, []);
			}
			clusterMap.get(root)!.push(polygons[i]);
		}

		return Array.from(clusterMap.values());
	}

	private readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => resolve(reader.result as ArrayBuffer);
			reader.onerror = () => reject(reader.error);
			reader.readAsArrayBuffer(file);
		});
	}

	private readFileAsText(file: File): Promise<string> {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => resolve(reader.result as string);
			reader.onerror = () => reject(reader.error);
			reader.readAsText(file);
		});
	}

	private isValidGeoJson(obj: any): boolean {
		if (!obj || typeof obj !== 'object') return false;

		// FeatureCollection
		if (obj.type === 'FeatureCollection' && Array.isArray(obj.features)) {
			return obj.features.some((f: any) => this.isPolygonFeature(f));
		}

		// Einzelnes Feature
		if (obj.type === 'Feature') {
			return this.isPolygonFeature(obj);
		}

		// Direkte Geometrie
		if (obj.type === 'Polygon' || obj.type === 'MultiPolygon') {
			return true;
		}

		return false;
	}

	private isPolygonFeature(feature: any): boolean {
		if (!feature?.geometry) return false;
		return feature.geometry.type === 'Polygon' || feature.geometry.type === 'MultiPolygon';
	}

	private extractCrs(geojson: any): string | null {
		// GeoJSON CRS-Property auslesen (z.B. "urn:ogc:def:crs:EPSG::5650")
		if (geojson.crs?.properties?.name) {
			const crsName = geojson.crs.properties.name;

			// Format: "urn:ogc:def:crs:EPSG::5650" oder "EPSG:5650"
			const epsgMatch = crsName.match(/EPSG::?(\d+)/i);
			if (epsgMatch) {
				return `EPSG:${epsgMatch[1]}`;
			}

			// PRJ/WKT-Format parsen - suche nach AUTHORITY["EPSG","XXXX"]
			const authorityMatch = crsName.match(/AUTHORITY\s*\[\s*"EPSG"\s*,\s*"(\d+)"\s*\]/i);
			if (authorityMatch) {
				return `EPSG:${authorityMatch[1]}`;
			}

			// Bekannte CRS-Namen aus WKT erkennen
			const wktCrsMapping: { [key: string]: string } = {
				'ETRS89_UTM_zone_32N': 'EPSG:25832',
				'ETRS89 / UTM zone 32N': 'EPSG:25832',
				'ETRS_1989_UTM_Zone_32N': 'EPSG:25832',
				'ETRS89_UTM_zone_33N': 'EPSG:25833',
				'ETRS89 / UTM zone 33N': 'EPSG:25833',
				'ETRS_1989_UTM_Zone_33N': 'EPSG:25833',
				'DHDN_3_Degree_Gauss_Zone_3': 'EPSG:31467',
				'DHDN / 3-degree Gauss-Kruger zone 3': 'EPSG:31467',
				'DHDN_3_Degree_Gauss_Zone_4': 'EPSG:31468',
				'DHDN / 3-degree Gauss-Kruger zone 4': 'EPSG:31468',
				'DHDN_3_Degree_Gauss_Zone_5': 'EPSG:31469',
				'DHDN / 3-degree Gauss-Kruger zone 5': 'EPSG:31469',
				'WGS_1984_UTM_Zone_32N': 'EPSG:32632',
				'WGS 84 / UTM zone 32N': 'EPSG:32632',
				'WGS_1984_UTM_Zone_33N': 'EPSG:32633',
				'WGS 84 / UTM zone 33N': 'EPSG:32633',
			};

			// Suche nach PROJCS["Name",...] oder GEOGCS["Name",...]
			const projcsMatch = crsName.match(/PROJCS\s*\[\s*"([^"]+)"/i);
			if (projcsMatch) {
				const projName = projcsMatch[1];
				if (wktCrsMapping[projName]) {
					return wktCrsMapping[projName];
				}
				// Versuche partielle Übereinstimmung
				for (const [key, value] of Object.entries(wktCrsMapping)) {
					if (projName.includes(key) || key.includes(projName)) {
						return value;
					}
				}
			}

			// Fallback: Suche nach UTM Zone in WKT
			const utmZoneMatch = crsName.match(/UTM[_\s]*[Zz]one[_\s]*(\d+)N/i);
			if (utmZoneMatch) {
				const zone = parseInt(utmZoneMatch[1]);
				// ETRS89 UTM für Deutschland (Zonen 32-33)
				if (crsName.includes('ETRS') || crsName.includes('European')) {
					return `EPSG:${25800 + zone}`;
				}
				// WGS84 UTM
				return `EPSG:${32600 + zone}`;
			}

			// Fallback: Suche nach Gauss-Kruger Zone
			const gkZoneMatch = crsName.match(/[Gg]auss[_\s\-]*[Kk]r[uü]ger[_\s]*[Zz]one[_\s]*(\d+)/i);
			if (gkZoneMatch) {
				const zone = parseInt(gkZoneMatch[1]);
				return `EPSG:${31464 + zone}`;
			}

			console.warn('CRS konnte nicht erkannt werden:', crsName.substring(0, 200));
		}
		return null; // Standard: WGS84
	}

	private extractPolygonsFromGeoJson(geojson: any, crs: string | null): Region[] {
		const regions: Region[] = [];

		const processGeometry = (geometry: any) => {
			// Frühzeitig abbrechen wenn zu viele Polygone
			if (regions.length > MAX_IMPORT_POLYGONS) {
				throw new Error(
					`Die Datei enthält mehr als ${MAX_IMPORT_POLYGONS} Polygone.\n\n` +
					`Bitte verwenden Sie eine Datei mit weniger Polygonen oder fassen Sie die Polygone in einer GIS-Software zusammen.`
				);
			}

			if (geometry.type === 'Polygon') {
				const polygon = this.coordinatesToLatLngs(geometry.coordinates[0], crs);
				if (polygon.length >= 3) {
					regions.push({
						type: 'polygon',
						bounds: getBoundsForPolygon(polygon),
						polygon: polygon
					});
				}
			} else if (geometry.type === 'MultiPolygon') {
				for (const poly of geometry.coordinates) {
					const polygon = this.coordinatesToLatLngs(poly[0], crs);
					if (polygon.length >= 3) {
						regions.push({
							type: 'polygon',
							bounds: getBoundsForPolygon(polygon),
							polygon: polygon
						});
					}
				}
			}
		};

		if (geojson.type === 'FeatureCollection') {
			for (const feature of geojson.features) {
				if (feature.geometry) {
					processGeometry(feature.geometry);
				}
			}
		} else if (geojson.type === 'Feature' && geojson.geometry) {
			processGeometry(geojson.geometry);
		} else if (geojson.type === 'Polygon' || geojson.type === 'MultiPolygon') {
			processGeometry(geojson);
		}

		return regions;
	}

	private coordinatesToLatLngs(coordinates: number[][], crs: string | null): LatLng[] {
		if (crs && crs !== 'EPSG:4326') {
			// Koordinaten aus anderem CRS transformieren
			const result: LatLng[] = [];

			// Erste Koordinate analysieren um Zonenkennung zu erkennen
			const firstCoord = coordinates[0];
			const { zonePrefix, mappedCrs } = this.detectZonePrefixAndMapCrs(firstCoord?.[0], crs);
			if (zonePrefix) {
				console.log(`Zonenkennung erkannt: ${zonePrefix} wird von X-Koordinaten abgezogen, CRS gemappt: ${crs} -> ${mappedCrs}`);
			}

			const targetCrs = mappedCrs || crs;

			for (const coord of coordinates) {
				try {
					let x = coord[0];
					const y = coord[1];

					// Zonenkennung entfernen falls erkannt
					if (zonePrefix) {
						x = x - zonePrefix;
					}

					const transformed = this.ts.from({ system: targetCrs, x: x, y: y } as any);
					if (transformed && isFinite(transformed.lat) && isFinite(transformed.lng)) {
						result.push(transformed);
					}
				} catch (e) {
					// Transformation fehlgeschlagen - Koordinate überspringen
					console.warn('Koordinatentransformation fehlgeschlagen:', coord, e);
				}
			}

			// Debug-Ausgabe
			if (result.length > 0) {
				console.log('Erste transformierte Koordinate:', result[0], 'aus', coordinates[0]);
			} else if (coordinates.length > 0) {
				console.error('Keine Koordinaten konnten transformiert werden! Erste Quellkoordinate:', coordinates[0]);
			}
			return result;
		}
		// Standard WGS84: GeoJSON verwendet [lng, lat], Leaflet verwendet [lat, lng]
		return coordinates.map(coord => new LatLng(coord[1], coord[0]));
	}

	/**
	 * Fügt einen Puffer (in Metern) um ein Polygon hinzu.
	 * Kleine Polygone werden zu Rechtecken mit Mindestgröße.
	 * Große Polygone behalten ihre Form mit radialem Puffer.
	 */
	private addBufferToPolygon(polygon: LatLng[], bufferMeters: number): LatLng[] {
		if (polygon.length < 3) return polygon;

		// Puffer in Grad umrechnen (ungefähr, für Mitteleuropa)
		const bufferDegreesLat = bufferMeters / 111000;
		const bufferDegreesLng = bufferMeters / 70000;

		// Bounds berechnen
		const bounds = getBoundsForPolygon(polygon);
		const width = (bounds.getEast() - bounds.getWest()) * 70000; // in Metern
		const height = (bounds.getNorth() - bounds.getSouth()) * 111000; // in Metern
		const minSize = bufferMeters * 2; // 400m

		// Kleine Polygone: Rechteck mit Mindestgröße
		if (width < minSize || height < minSize) {
			const center = bounds.getCenter();
			const halfLat = Math.max(bufferDegreesLat, (bounds.getNorth() - bounds.getSouth()) / 2 + bufferDegreesLat);
			const halfLng = Math.max(bufferDegreesLng, (bounds.getEast() - bounds.getWest()) / 2 + bufferDegreesLng);
			return [
				new LatLng(center.lat - halfLat, center.lng - halfLng),
				new LatLng(center.lat - halfLat, center.lng + halfLng),
				new LatLng(center.lat + halfLat, center.lng + halfLng),
				new LatLng(center.lat + halfLat, center.lng - halfLng)
			];
		}

		// Große Polygone: Radialer Puffer vom Zentroid
		const centroid = computeCentroid(polygon);
		return polygon.map(point => {
			const dLat = point.lat - centroid.lat;
			const dLng = point.lng - centroid.lng;
			const dist = Math.sqrt(dLat * dLat + dLng * dLng);
			if (dist === 0) return point;

			const dirLat = dLat / dist;
			const dirLng = dLng / dist;
			return new LatLng(
				point.lat + dirLat * bufferDegreesLat,
				point.lng + dirLng * bufferDegreesLng
			);
		});
	}

	/**
	 * Erkennt das Koordinatensystem aus dem PRJ-Text.
	 * Unterstützt gängige deutsche und europäische Projektionen.
	 */
	private detectCrsFromPrj(prjText: string): string | null {
		const upper = prjText.toUpperCase();

		// ETRS89/UTM Zonen
		if (upper.includes('ETRS') || upper.includes('EUROPEAN_TERRESTRIAL')) {
			if (upper.includes('ZONE_31') || upper.includes('ZONE31')) return 'EPSG:25831';
			if (upper.includes('ZONE_32') || upper.includes('ZONE32')) return 'EPSG:25832';
			if (upper.includes('ZONE_33') || upper.includes('ZONE33')) return 'EPSG:25833';
			if (upper.includes('ZONE_34') || upper.includes('ZONE34')) return 'EPSG:25834';
		}

		// WGS84/UTM Zonen
		if (upper.includes('WGS') && upper.includes('UTM')) {
			if (upper.includes('ZONE_31') || upper.includes('ZONE31')) return 'EPSG:32631';
			if (upper.includes('ZONE_32') || upper.includes('ZONE32')) return 'EPSG:32632';
			if (upper.includes('ZONE_33') || upper.includes('ZONE33')) return 'EPSG:32633';
			if (upper.includes('ZONE_34') || upper.includes('ZONE34')) return 'EPSG:32634';
		}

		// Gauß-Krüger (DHDN)
		if (upper.includes('GAUSS') || upper.includes('GK') || upper.includes('DHDN')) {
			if (upper.includes('ZONE_2') || upper.includes('ZONE2') || upper.includes('_2_')) return 'EPSG:31466';
			if (upper.includes('ZONE_3') || upper.includes('ZONE3') || upper.includes('_3_')) return 'EPSG:31467';
			if (upper.includes('ZONE_4') || upper.includes('ZONE4') || upper.includes('_4_')) return 'EPSG:31468';
			if (upper.includes('ZONE_5') || upper.includes('ZONE5') || upper.includes('_5_')) return 'EPSG:31469';
		}

		// WGS84 (geografisch)
		if (upper.includes('WGS_1984') || upper.includes('WGS84') || upper.includes('WGS 84')) {
			if (!upper.includes('UTM')) {
				return 'EPSG:4326';
			}
		}

		// Web Mercator
		if (upper.includes('WEB_MERCATOR') || upper.includes('PSEUDO_MERCATOR') || upper.includes('3857')) {
			return 'EPSG:3857';
		}

		// EPSG-Code direkt im PRJ
		const epsgMatch = prjText.match(/EPSG[:\s]*(\d{4,5})/i);
		if (epsgMatch) {
			return `EPSG:${epsgMatch[1]}`;
		}

		// Authority-Code (z.B. "AUTHORITY["EPSG","25833"]")
		const authorityMatch = prjText.match(/AUTHORITY\s*\[\s*"EPSG"\s*,\s*"?(\d+)"?\s*\]/i);
		if (authorityMatch) {
			return `EPSG:${authorityMatch[1]}`;
		}

		console.warn('CRS konnte nicht aus PRJ erkannt werden:', prjText.substring(0, 150));
		return null;
	}

	/**
	 * Erkennt automatisch ob eine Zonenkennung in der X-Koordinate vorhanden ist.
	 * Gibt den Wert zurück, der abgezogen werden muss (z.B. 33000000 für Zone 33),
	 * sowie das gemappte CRS für die Transformation.
	 */
	private detectZonePrefixAndMapCrs(x: number | undefined, crs: string | null): { zonePrefix: number | null, mappedCrs: string | null } {
		if (!x || !crs) return { zonePrefix: null, mappedCrs: null };

		// EPSG:5650 = ETRS89/UTM zone 33N (zE-N) - hat Zonenkennung 33 im Rechtswert
		// Muss auf EPSG:25833 gemappt werden nach Abzug der Zonenkennung
		if (crs === 'EPSG:5650' && x > 33000000 && x < 34000000) {
			return { zonePrefix: 33000000, mappedCrs: 'EPSG:25833' };
		}

		// EPSG:5649 = ETRS89/UTM zone 32N (zE-N) - hat Zonenkennung 32 im Rechtswert
		if (crs === 'EPSG:5649' && x > 32000000 && x < 33000000) {
			return { zonePrefix: 32000000, mappedCrs: 'EPSG:25832' };
		}

		// EPSG:5651 = ETRS89/UTM zone 31N (zE-N) - hat Zonenkennung 31 im Rechtswert
		if (crs === 'EPSG:5651' && x > 31000000 && x < 32000000) {
			return { zonePrefix: 31000000, mappedCrs: 'EPSG:25831' };
		}

		// EPSG:5652 = ETRS89/UTM zone 34N (zE-N) - hat Zonenkennung 34 im Rechtswert
		if (crs === 'EPSG:5652' && x > 34000000 && x < 35000000) {
			return { zonePrefix: 34000000, mappedCrs: 'EPSG:25834' };
		}

		// UTM-Zonen (EPSG:258xx, EPSG:326xx, EPSG:327xx)
		// Normale UTM X-Koordinaten liegen zwischen 100.000 und 900.000
		// Mit Zonenkennung: z.B. 32500000 + 500000 = 33000000

		// ETRS89/UTM Zone 32N (EPSG:25832)
		if ((crs === 'EPSG:25832' || crs === 'EPSG:32632') && x > 32000000 && x < 33000000) {
			return { zonePrefix: 32000000, mappedCrs: null };
		}

		// ETRS89/UTM Zone 33N (EPSG:25833)
		if ((crs === 'EPSG:25833' || crs === 'EPSG:32633') && x > 33000000 && x < 34000000) {
			return { zonePrefix: 33000000, mappedCrs: null };
		}

		// ETRS89/UTM Zone 31N (EPSG:25831)
		if ((crs === 'EPSG:25831' || crs === 'EPSG:32631') && x > 31000000 && x < 32000000) {
			return { zonePrefix: 31000000, mappedCrs: null };
		}

		// ETRS89/UTM Zone 34N (EPSG:25834)
		if ((crs === 'EPSG:25834' || crs === 'EPSG:32634') && x > 34000000 && x < 35000000) {
			return { zonePrefix: 34000000, mappedCrs: null };
		}

		// Gauß-Krüger Zone 3 (EPSG:31467) - Rechtswert beginnt normalerweise mit 3
		// Mit Zonenkennung wäre es z.B. 3500000, ohne wäre es 500000
		if (crs === 'EPSG:31467' && x > 3000000 && x < 4000000) {
			// Prüfen ob es eine echte GK-Koordinate ist (mit 3er-Präfix) oder eine mit zusätzlichem Präfix
			// Normale GK3-Koordinaten: 3.280.000 bis 3.920.000
			// Hier ist keine Anpassung nötig, da 3 der Zonenkenner ist
			return { zonePrefix: null, mappedCrs: null };
		}

		// Gauß-Krüger Zone 4 (EPSG:31468)
		if (crs === 'EPSG:31468' && x > 4000000 && x < 5000000) {
			return { zonePrefix: null, mappedCrs: null }; // Normale GK4-Koordinaten haben bereits die 4 als Präfix
		}

		// Generische Erkennung für unbekannte UTM-Zonen
		// Wenn X > 30.000.000, könnte es eine Zonenkennung sein
		if (x > 30000000 && x < 40000000) {
			const possibleZone = Math.floor(x / 1000000);
			console.warn(`Möglicherweise unbekannte UTM-Zone ${possibleZone} in CRS ${crs}`);
			// Versuche automatische Korrektur - mappe auf EPSG:258xx
			const mappedCrs = `EPSG:258${possibleZone}`;
			return { zonePrefix: possibleZone * 1000000, mappedCrs };
		}

		return { zonePrefix: null, mappedCrs: null };
	}

	/**
	 * Zeigt das Modal zur Auswahl von GPKG-Layern an.
	 * Nur Feature-Layer (keine Tile-Layer) für Bereichs-Import.
	 */
	private async showGpkgLayerSelectModal(filename: string, geoPackage: any, featureTables: string[]): Promise<GpkgLayerInfo[] | null> {
		// Geometrietyp für Feature-Layer ermitteln
		const getGeometryType = (tableName: string): GpkgGeometryType => {
			try {
				const featureDao = geoPackage.getFeatureDao(tableName);
				const geomColumn = featureDao.geometryColumns;
				if (geomColumn?.geometry_type_name) {
					const gpkgType = geomColumn.geometry_type_name.toUpperCase();
					if (gpkgType.includes('POINT')) return gpkgType.includes('MULTI') ? 'MultiPoint' : 'Point';
					if (gpkgType.includes('LINE')) return gpkgType.includes('MULTI') ? 'MultiLineString' : 'LineString';
					if (gpkgType.includes('POLYGON')) return gpkgType.includes('MULTI') ? 'MultiPolygon' : 'Polygon';
					if (gpkgType.includes('GEOMETRY')) return 'GeometryCollection';
				}
			} catch (e) {
				console.warn(`Konnte Geometrietyp für ${tableName} nicht ermitteln:`, e);
			}
			return 'unknown';
		};

		// Layer-Liste für das Modal vorbereiten (nur Features, keine Tiles für Bereichs-Import)
		const layers: GpkgLayerInfo[] = featureTables.map(name => ({
			name,
			type: 'feature' as const,
			geometryType: getGeometryType(name),
			selected: true
		}));

		if (layers.length === 0) {
			return null;
		}

		const modalRef = this.modalService.open(GpkgLayerSelectModalComponent, {
			centered: true,
			backdrop: 'static'
		});

		modalRef.componentInstance.filename = filename;
		modalRef.componentInstance.layers = layers;

		try {
			return await modalRef.result;
		} catch {
			// Modal wurde abgebrochen
			return null;
		}
	}

}
